import type { Locale } from "../i18n/locale";
import type { AudienceMode } from "./audienceMode";
import { DEFAULT_AUDIENCE_MODE } from "./audienceMode";
import {
  computeProfileAssumptionNote,
  costAssumptions,
  costLineCopy,
  regionAssumptionNote,
  savingsLeverNotes,
} from "../i18n/costCopy";
import {
  computeProfileFactors,
  DEFAULT_COMPUTE_PROFILE,
  type ComputeProfileId,
} from "./computeProfile";
import type { AwsRegionId } from "./awsRegion";
import { awsRegionFactor } from "./awsRegion";
import {
  DEFAULT_DEPLOYMENT_STACK,
  deploymentCostAdjust,
  deploymentStackLineItems,
  type DeploymentStackConfig,
} from "./deploymentStack";
import {
  DEFAULT_LLM_PRICE_PROFILE,
  llmProfileFactor,
  type LlmPriceProfile,
} from "./llmPriceProfile";
import {
  applySavingsDynamo,
  applySavingsLlm,
  applySavingsToLambdaLine,
  DEFAULT_SAVINGS_LEVERS,
  type SavingsLevers,
} from "./savingsLevers";
import type { CostEstimate, LineItem, ScalingPoint, Workload } from "./types";

/** Approximate public list prices (USD). Tune in one place. */
const PRICING = {
  apiGatewayPerMillion: 3.5,
  lambdaPerMillionRequests: 0.2,
  /** $ per GB-second (x86, us-east-1 ballpark) */
  lambdaGbSecond: 0.0000166667,
  lambdaMemoryGb: 0.5,
  lambdaAvgSeconds: 0.25,
  dynamoWritePerMillion: 1.25,
  dynamoReadPerMillion: 0.25,
  /** Blended $ per 1M tokens (rough gpt-4o-mini class) */
  llmInputPer1M: 0.15,
  llmOutputPer1M: 0.6,
  cloudwatchPerGbIngest: 0.5,
  cloudwatchMetricsMonthly: 10,
  dataTransferEgressPerGb: 0.09,
  /** S3 Standard storage, us-east-1 ballpark ($/GB-mo). */
  s3StandardPerGbMonth: 0.023,
} as const;

const DAYS_PER_MONTH = 30;

export function estimateMonthlyCost(
  w: Workload,
  locale: Locale = "uk",
  region: AwsRegionId = "us-east-1",
  savings: SavingsLevers = DEFAULT_SAVINGS_LEVERS,
  stack: DeploymentStackConfig = DEFAULT_DEPLOYMENT_STACK,
  llmProfile: LlmPriceProfile = DEFAULT_LLM_PRICE_PROFILE,
  audience: AudienceMode = DEFAULT_AUDIENCE_MODE,
  computeProfile: ComputeProfileId = DEFAULT_COMPUTE_PROFILE,
): CostEstimate {
  const factor = awsRegionFactor(region);
  const { lambdaFactor, dynamoFactor } = deploymentCostAdjust(stack);
  const { lambdaMult, transferMult } = computeProfileFactors(computeProfile);
  const gpuAssumption = computeProfileAssumptionNote(locale, computeProfile);
  const assumptions = [
    ...costAssumptions(locale, audience),
    regionAssumptionNote(locale, region),
    ...(gpuAssumption ? [gpuAssumption] : []),
    ...savingsLeverNotes(locale, savings),
  ];

  const dailyRequests = w.usersDaily * w.requestsPerUser;
  const monthlyRequests = dailyRequests * DAYS_PER_MONTH;

  const apiGateway = (monthlyRequests / 1e6) * PRICING.apiGatewayPerMillion;

  const lambdaInvocations = monthlyRequests;
  const gbSeconds =
    lambdaInvocations * PRICING.lambdaMemoryGb * PRICING.lambdaAvgSeconds;
  const lambdaCompute = gbSeconds * PRICING.lambdaGbSecond;
  const lambdaRequest = (lambdaInvocations / 1e6) * PRICING.lambdaPerMillionRequests;
  const lambda =
    applySavingsToLambdaLine(lambdaRequest, lambdaCompute, savings) *
    lambdaFactor *
    lambdaMult;

  const writesPerMonth = estimateDynamoWrites(w, dailyRequests);
  const readsPerMonth = estimateDynamoReads(w, dailyRequests);
  const dynamoRaw =
    (writesPerMonth / 1e6) * PRICING.dynamoWritePerMillion +
    (readsPerMonth / 1e6) * PRICING.dynamoReadPerMillion;
  const dynamo = applySavingsDynamo(dynamoRaw, savings) * dynamoFactor;

  const s3Gb =
    w.enabledFeatures.includes("files") && w.storageKbPerUser > 0
      ? (w.usersDaily * w.storageKbPerUser) / (1024 * 1024)
      : 0;
  const s3StorageUsd = s3Gb * PRICING.s3StandardPerGbMonth;

  const aiCallsMonthly = monthlyRequests * w.aiRequestShare;
  const inputTokens = aiCallsMonthly * w.avgPromptTokens;
  const outputTokens = aiCallsMonthly * w.avgCompletionTokens;
  const openaiRaw =
    ((inputTokens / 1e6) * PRICING.llmInputPer1M +
      (outputTokens / 1e6) * PRICING.llmOutputPer1M) *
    llmProfileFactor(llmProfile);
  const openai = applySavingsLlm(openaiRaw, savings);

  const logGbMonth = monthlyRequests * 0.000_002 + 0.5;
  const cloudwatch =
    logGbMonth * PRICING.cloudwatchPerGbIngest + PRICING.cloudwatchMetricsMonthly;

  const egressGb = monthlyRequests * 0.000_05 + w.usersDaily * DAYS_PER_MONTH * 0.001;
  const transfer = egressGb * PRICING.dataTransferEgressPerGb * transferMult;

  const copy = costLineCopy(locale, {
    monthlyRequests,
    lambdaInvocations,
    lambdaMemoryGb: PRICING.lambdaMemoryGb,
    readsPerMonth,
    writesPerMonth,
    aiCallsMonthly,
    avgPromptTokens: w.avgPromptTokens,
    avgCompletionTokens: w.avgCompletionTokens,
    s3Gb,
  });

  const lineItems: LineItem[] = [
    {
      id: "apigw",
      label: copy.apigw.label,
      monthlyUsd: apiGateway,
      detail: copy.apigw.detail,
    },
    {
      id: "lambda",
      label: copy.lambda.label,
      monthlyUsd: lambda,
      detail: copy.lambda.detail,
    },
    {
      id: "dynamo",
      label: copy.dynamo.label,
      monthlyUsd: dynamo,
      detail: copy.dynamo.detail,
    },
    ...(s3StorageUsd > 0
      ? [
          {
            id: "s3" as const,
            label: copy.s3.label,
            monthlyUsd: s3StorageUsd,
            detail: copy.s3.detail,
          },
        ]
      : []),
    {
      id: "llm",
      label: copy.llm.label,
      monthlyUsd: openai,
      detail: copy.llm.detail,
    },
    {
      id: "cw",
      label: copy.cw.label,
      monthlyUsd: cloudwatch,
      detail: copy.cw.detail,
    },
    {
      id: "xfer",
      label: copy.xfer.label,
      monthlyUsd: transfer,
      detail: copy.xfer.detail,
    },
  ];

  const scaled: LineItem[] = lineItems.map((row) => ({
    ...row,
    monthlyUsd: row.monthlyUsd * factor,
  }));

  const deploy = deploymentStackLineItems(stack, w, locale, savings);
  const scaledDeploy: LineItem[] = deploy.items.map((row) => ({
    ...row,
    monthlyUsd: row.monthlyUsd * factor,
  }));
  const cfItems = scaledDeploy.filter((x) => x.id === "cloudfront");
  const tailDeploy = scaledDeploy.filter((x) => x.id !== "cloudfront");
  const lineItemsAll = [...scaled];
  const apigwIdx = lineItemsAll.findIndex((x) => x.id === "apigw");
  if (cfItems.length > 0 && apigwIdx >= 0) {
    lineItemsAll.splice(apigwIdx + 1, 0, ...cfItems);
  }
  lineItemsAll.push(...tailDeploy);
  const totalMonthlyUsd = lineItemsAll.reduce((s, x) => s + x.monthlyUsd, 0);

  return {
    lineItems: lineItemsAll,
    totalMonthlyUsd,
    assumptions: [...assumptions, ...deploy.assumptions],
  };
}

function estimateDynamoWrites(w: Workload, dailyRequests: number): number {
  let mult = 0.15;
  if (w.enabledFeatures.includes("chat")) mult += 0.35;
  if (w.enabledFeatures.includes("storage")) mult += 0.2;
  if (w.enabledFeatures.includes("search")) mult += 0.15;
  return dailyRequests * mult * DAYS_PER_MONTH;
}

function estimateDynamoReads(w: Workload, dailyRequests: number): number {
  let mult = 0.5;
  if (w.enabledFeatures.includes("search")) mult += 1.2;
  if (w.enabledFeatures.includes("chat")) mult += 0.8;
  return dailyRequests * mult * DAYS_PER_MONTH;
}

export function buildScalingCurve(
  base: Workload,
  multipliers: number[],
  locale: Locale = "uk",
  region: AwsRegionId = "us-east-1",
  savings: SavingsLevers = DEFAULT_SAVINGS_LEVERS,
  stack: DeploymentStackConfig = DEFAULT_DEPLOYMENT_STACK,
  llmProfile: LlmPriceProfile = DEFAULT_LLM_PRICE_PROFILE,
  audience: AudienceMode = DEFAULT_AUDIENCE_MODE,
  computeProfile: ComputeProfileId = DEFAULT_COMPUTE_PROFILE,
): ScalingPoint[] {
  return multipliers.map((m) => {
    const w = { ...base, usersDaily: Math.round(base.usersDaily * m) };
    const { totalMonthlyUsd } = estimateMonthlyCost(
      w,
      locale,
      region,
      savings,
      stack,
      llmProfile,
      audience,
      computeProfile,
    );
    return { usersDaily: w.usersDaily, totalMonthlyUsd };
  });
}

export function applyWorkloadOverrides(
  base: Workload,
  overrides: Partial<Pick<Workload, "usersDaily" | "requestsPerUser">>,
): Workload {
  return {
    ...base,
    ...overrides,
    usersDaily: Math.max(1, overrides.usersDaily ?? base.usersDaily),
    requestsPerUser: Math.max(1, overrides.requestsPerUser ?? base.requestsPerUser),
  };
}
